import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import api from "@/lib/api";
import Breadcrumbs from "@/components/admin/Breadcrumbs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { formatApiError } from "@/lib/errors";

export default function AdminVehicles() {
  const [rows, setRows] = useState([]); const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false); const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [form, setForm] = useState({ registration:"", make:"", model:"", notes:"" });

  const load = async () => { setLoading(true); try { const { data } = await api.get("/admin/vehicles"); setRows(data); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const open = (v) => { setEditing(v); setForm(v ? { registration:v.registration, make:v.make, model:v.model, notes:v.notes||"" } : { registration:"", make:"", model:"", notes:"" }); setFormOpen(true); };
  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.patch(`/admin/vehicles/${editing.id}`, form);
      else await api.post("/admin/vehicles", form);
      toast.success(editing ? "Vehicle updated" : "Vehicle added");
      setFormOpen(false); load();
    } catch (err) { toast.error(formatApiError(err) || "Save failed"); }
  };
  const retire = async (v) => { try { await api.patch(`/admin/vehicles/${v.id}`, { status: "retired" }); toast.success("Retired"); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const activate = async (v) => { try { await api.patch(`/admin/vehicles/${v.id}`, { status: "active" }); toast.success("Re-activated"); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const doDelete = async () => { const v = confirm; setConfirm(null); if (!v) return; try { const { data } = await api.delete(`/admin/vehicles/${v.id}`); toast.success(data.mode === "retired" ? "Retired (entries exist)" : "Deleted"); load(); } catch (e) { toast.error(formatApiError(e) || "Failed"); } };

  return (
    <div data-testid="admin-vehicles" className="mx-auto w-full max-w-5xl">
      <Breadcrumbs />
      <div className="mb-6 flex items-center justify-between">
        <div><h2 className="font-display text-2xl font-semibold text-slate-900">Vehicles</h2>
          <p className="mt-1 text-sm text-slate-500">{rows.length} total · {rows.filter(r=>r.status==="active").length} active.</p></div>
        <Button onClick={() => open(null)} data-testid="vehicles-create-btn" className="gap-2 bg-cargodash-primary-600 hover:bg-cargodash-primary-700"><Plus className="h-4 w-4" />New vehicle</Button>
      </div>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <Table><TableHeader><TableRow>
          <TableHead className="pl-4">Registration</TableHead><TableHead>Make / Model</TableHead>
          <TableHead>Status</TableHead><TableHead>Notes</TableHead>
          <TableHead className="pr-4 text-right">Actions</TableHead>
        </TableRow></TableHeader><TableBody>
          {loading && <TableRow><TableCell colSpan={5} className="py-10 text-center text-sm text-slate-500">Loading…</TableCell></TableRow>}
          {!loading && rows.length===0 && <TableRow><TableCell colSpan={5} className="py-10 text-center text-sm text-slate-500">No vehicles yet.</TableCell></TableRow>}
          {rows.map(v => (
            <TableRow key={v.id} data-testid={`vehicle-row-admin-${v.id}`}>
              <TableCell className="pl-4 font-mono font-semibold">{v.registration}</TableCell>
              <TableCell>{v.make} {v.model}</TableCell>
              <TableCell>{v.status==="active" ? <Badge className="bg-cargodash-primary-50 text-cargodash-primary-700 hover:bg-cargodash-primary-50">Active</Badge> : <Badge variant="secondary">Retired</Badge>}</TableCell>
              <TableCell className="max-w-xs truncate text-xs text-slate-500">{v.notes || "—"}</TableCell>
              <TableCell className="pr-4 text-right"><div className="flex justify-end gap-1">
                <Button variant="ghost" size="icon" onClick={() => open(v)} data-testid={`vehicle-edit-${v.id}`}><Pencil className="h-4 w-4" /></Button>
                {v.status==="active" ? <Button variant="ghost" size="sm" onClick={() => retire(v)} data-testid={`vehicle-retire-${v.id}`} className="text-xs">Retire</Button>
                                     : <Button variant="ghost" size="sm" onClick={() => activate(v)} data-testid={`vehicle-activate-${v.id}`} className="text-xs">Activate</Button>}
                <Button variant="ghost" size="icon" onClick={() => setConfirm(v)} data-testid={`vehicle-delete-${v.id}`} className="text-cargodash-alert-600 hover:bg-cargodash-alert-600/10 hover:text-cargodash-alert-600"><Trash2 className="h-4 w-4" /></Button>
              </div></TableCell>
            </TableRow>
          ))}
        </TableBody></Table>
      </div>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-md" data-testid="vehicle-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit vehicle" : "Register vehicle"}</DialogTitle>
            <DialogDescription>Registration numbers are normalised to upper-case.</DialogDescription></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div className="space-y-2"><Label>Registration</Label><Input required value={form.registration} onChange={e=>setForm({...form,registration:e.target.value})} data-testid="vehicle-form-reg" className="font-mono uppercase" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Make</Label><Input required value={form.make} onChange={e=>setForm({...form,make:e.target.value})} data-testid="vehicle-form-make" /></div>
              <div className="space-y-2"><Label>Model</Label><Input required value={form.model} onChange={e=>setForm({...form,model:e.target.value})} data-testid="vehicle-form-model" /></div>
            </div>
            <div className="space-y-2"><Label>Notes</Label><Textarea value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} rows={2} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="vehicle-form-submit" className="bg-cargodash-primary-600 hover:bg-cargodash-primary-700">{editing ? "Save" : "Register"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirm} onOpenChange={o => !o && setConfirm(null)}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete {confirm?.registration}?</AlertDialogTitle>
          <AlertDialogDescription>Vehicles with any calendar entries are retired instead of deleted so history is preserved.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-cargodash-alert-600 hover:bg-cargodash-alert-600/90">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
